import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EventsAPI, EventPollsAPI } from '../api';
import { UserContext } from '../context/UserContext';
import { Link } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { resolveMediaUrl } from '../utils/url';

export default function EventsPage() {
  const { user } = useContext(UserContext);
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');
  const canManageEvents = isAdmin || (Array.isArray(user?.permissions) && user.permissions.includes('events.manage'));

  // filters
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [type, setType] = useState('');
  const [place, setPlace] = useState(''); // '' | 'online' | 'venue'
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [hasCapacity, setHasCapacity] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [sortBy, setSortBy] = useState('soonest'); // 'soonest' | 'newest' | 'title'

  // paging
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  // quick view state (since dedicated detail route isn't usable currently)
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [comments, setComments] = useState([]);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [selError, setSelError] = useState('');
  const [polls, setPolls] = useState([]);
  const [logs, setLogs] = useState([]);

  async function openQuickView(id) {
    try {
      setSelectedId(id);
      setLoadingSelected(true);
      setSelError('');
      const ev = await EventsAPI.get(id);
      setSelected(ev);
      try {
        const a = await EventsAPI.attendees(id);
        setAttendees(a?.items || []);
      } catch {}
      try {
        const c = await EventsAPI.comments(id);
        setComments(c?.items || []);
      } catch {}
      try {
        const p = await EventPollsAPI.list(id);
        setPolls(p?.items || []);
      } catch {}
      try {
        const l = await EventsAPI.logs(id);
        setLogs(l?.items || []);
      } catch {}
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setSelError('Bitte einloggen');
      else if (status === 403) setSelError('Keine Berechtigung oder privat');
      else if (e?.code === 'ERR_NETWORK') setSelError('Server nicht erreichbar');
      else setSelError('Details konnten nicht geladen werden');
    } finally {
      setLoadingSelected(false);
    }
  }

  async function doAction(action) {
    if (!selectedId) return;
    try {
      await EventsAPI.register(selectedId, action);
      await openQuickView(selectedId);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Aktion fehlgeschlagen';
      alert(msg);
    }
  }

  async function load() {
    try {
      setLoading(true);
      setError('');
      const res = await EventsAPI.list({ q, tag, type, place, from, to, hasCapacity, onlyMine, page, pageSize });
      const now = new Date();
      let list = res?.items || [];
      if (!showPast) {
        list = list.filter(ev => {
          const start = new Date(ev.startAt);
          const end = ev.endAt ? new Date(ev.endAt) : null;
          return end ? end >= now : start >= now;
        });
      }
      list = [...list].sort((a,b) => {
        if (sortBy === 'title') return String(a.title||'').localeCompare(String(b.title||''));
        const as = new Date(a.startAt).getTime();
        const bs = new Date(b.startAt).getTime();
        if (sortBy === 'newest') return bs - as; // latest start first
        return as - bs; // soonest first
      });
      setItems(list);
      setTotal(list.length);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Bitte einloggen');
      else if (status === 403) setError('Keine Berechtigung');
      else if (e?.code === 'ERR_NETWORK') setError('Server nicht erreichbar');
      else setError('Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    load();
  }

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Events</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>Anstehende und vergangene Veranstaltungen. Suche, filtere und registriere dich.</p>
      </section>

      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:16 }}>
          {/* Left: list + filters */}
          <div>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:10 }}>
              <form onSubmit={onSearch} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:8 }}>
                <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Suche (Titel/Beschreibung)" />
                <input value={tag} onChange={e=>setTag(e.target.value)} placeholder="Tag/Kategorie" />
                <input value={type} onChange={e=>setType(e.target.value)} placeholder="Event-Typ (z.B. Turnier)" />
                <select value={place} onChange={e=>setPlace(e.target.value)}>
                  <option value="">Ort/Online</option>
                  <option value="online">Online</option>
                  <option value="venue">Ort</option>
                </select>
                <input type="date" value={from} onChange={e=>setFrom(e.target.value)} />
                <input type="date" value={to} onChange={e=>setTo(e.target.value)} />
                <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={hasCapacity} onChange={e=>setHasCapacity(e.target.checked)} /> freie Plätze</label>
                <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={onlyMine} onChange={e=>setOnlyMine(e.target.checked)} /> nur eigene</label>
                <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={showPast} onChange={e=>setShowPast(e.target.checked)} /> vergangene einblenden</label>
                <select value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                  <option value="soonest">Sortieren: Nächster zuerst</option>
                  <option value="newest">Neueste zuerst</option>
                  <option value="title">A–Z Titel</option>
                </select>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="submit">Filtern</button>
                  <button type="button" onClick={()=>{ setQ(''); setTag(''); setType(''); setPlace(''); setFrom(''); setTo(''); setHasCapacity(false); setOnlyMine(false); setShowPast(false); setSortBy('soonest'); setPage(1); load(); }}>Zurücksetzen</button>
                </div>
              </form>
            </div>

            <div style={{ marginBottom: 12, color: '#cbd5e1', fontSize: 13 }}>
              {loading ? 'Lade…' : `${total} Einträge`}
              {error && <span style={{ color: '#f87171', marginLeft: 12 }}>{error}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
              {items.map(ev => (
                <EventCard key={ev.id} ev={ev} onOpen={() => openQuickView(ev.id)} />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <button disabled={page<=1} onClick={()=>setPage(p => Math.max(1, p-1))}>Zurück</button>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>Seite {page}</div>
              <button disabled={(page*pageSize)>=total} onClick={()=>setPage(p => p+1)}>Weiter</button>
              <select value={pageSize} onChange={e=>setPageSize(parseInt(e.target.value,10))}>
                {[12,24,36,60].map(n => <option key={n} value={n}>{n}/Seite</option>)}
              </select>
            </div>
          </div>

          {/* Right: admin create shortcut */}
          <div>
            {selectedId ? (
              <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                  <h3 style={{ margin:'8px 0' }}>{selected?.title || 'Event'}</h3>
                  <button onClick={()=>{ setSelectedId(null); setSelected(null); setAttendees([]); }}>Schließen</button>
                </div>
                {loadingSelected ? (
                  <div style={{ color:'#9ca3af' }}>Lade…</div>
                ) : selError ? (
                  <div style={{ color:'#f87171' }}>{selError}</div>
                ) : selected ? (
                  <QuickViewContent ev={selected} attendees={attendees} comments={comments} setComments={setComments} polls={polls} setPolls={setPolls} logs={logs} onAction={doAction} currentUser={user} reload={()=>openQuickView(selected.id)} />
                ) : (
                  <div style={{ color:'#9ca3af' }}>Keine Daten</div>
                )}
              </div>
            ) : (
              canManageEvents && (
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                  <h3 style={{ margin:'8px 0' }}>Veranstaltungen verwalten</h3>
                  <Link to="/events/new"><button>Neues Event anlegen</button></Link>
                  <div style={{ color:'#9ca3af', fontSize:12, marginTop:8 }}>
                    Tipp: Im Event kannst du Sichtbarkeit (öffentlich/Community/privat), Kapazität & Warteliste sowie Online-Link oder Adresse pflegen.
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function formatDateRange(startAt, endAt) {
  try {
    const s = new Date(startAt);
    const e = endAt ? new Date(endAt) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const sStr = new Intl.DateTimeFormat(undefined, opts).format(s);
    const eStr = e ? new Intl.DateTimeFormat(undefined, opts).format(e) : null;
    return eStr ? `${sStr} – ${eStr}` : sStr;
  } catch { return String(startAt); }
}

function EventCard({ ev, onOpen }) {
  const startsSoon = !!ev.beginsSoon;
  const lowCap = !!ev.lowCapacity;
  const hasCap = !ev.capacity || (ev.counts?.confirmed ?? 0) < ev.capacity;
  const capStr = ev.capacity ? `${ev.counts?.confirmed || 0}/${ev.capacity}` : 'unbegrenzt';
  const regMode = ev.registrationMode || 'open';
  const now = Date.now();
  const startMs = new Date(ev.startAt).getTime();
  const diffMs = Math.max(0, startMs - now);
  const diffH = Math.floor(diffMs / (1000*60*60));
  const diffD = Math.floor(diffH / 24);
  const countdown = diffD > 0 ? `in ${diffD} Tag${diffD===1?'':'en'}` : (diffH > 0 ? `in ${diffH} Std` : null);
  return (
    <div style={{ border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, overflow:'hidden', background:'rgba(255,255,255,0.03)' }}>
      {ev.posterUrl && (
        <Link to={`/events/${ev.id}`}>
          <div style={{ width:'100%', height:180, background:'#111', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <img src={resolveMediaUrl(ev.posterUrl)} alt={ev.title} style={{ maxWidth:'100%', maxHeight:'100%', width:'auto', height:'auto' }} />
          </div>
        </Link>
      )}
      <div style={{ padding:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
          <div style={{ fontWeight:700 }}>{ev.title}</div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {startsSoon && <span title="Start in <24h" style={{ fontSize:12, color:'#f59e0b' }}>⏳</span>}
            {countdown && <span style={{ fontSize:12, color:'#fbbf24' }}>{countdown}</span>}
            {lowCap && <span title="Wenige Plätze frei" style={{ fontSize:12, color:'#ef4444' }}>⚠️ Letzte Plätze</span>}
          </div>
        </div>
        <div style={{ color:'#9ca3af', fontSize:13, marginTop:4 }}>{formatDateRange(ev.startAt, ev.endAt)}</div>
        <div style={{ color:'#9ca3af', fontSize:12, marginTop:2 }}>Teilnahme: {ev.visibility}</div>
        <div style={{ color:'#9ca3af', fontSize:12, marginTop:2 }}>Kapazität: {capStr}</div>
        {Array.isArray(ev.tags) && ev.tags.length > 0 && (
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
            {ev.tags.slice(0,4).map((t,idx) => (
              <span key={idx} style={{ fontSize: 12, background:'rgba(145,70,255,0.15)', color:'#c084fc', border:'1px solid rgba(145,70,255,0.35)', padding:'2px 6px', borderRadius:6 }}>{t}</span>
            ))}
          </div>
        )}
        <div style={{ display:'flex', gap:8, marginTop:10, alignItems:'center' }}>
          <button onClick={onOpen}>Details</button>
          {hasCap ? <span title="Plätze verfügbar" style={{ fontSize:12, color:'#22c55e' }}>● Jetzt anmelden</span> : <span title="Ausgebucht" style={{ fontSize:12, color:'#ef4444' }}>● Ausgebucht</span>}
        </div>
      </div>
    </div>
  );
}

function QuickViewContent({ ev, attendees, comments, setComments, polls, setPolls, logs, onAction, currentUser, reload }) {
  const isAdmin = Array.isArray(currentUser?.roles) && currentUser.roles.includes('admin');
  const isOwner = currentUser?.id === ev.ownerId;
  const ended = ev.endAt ? new Date(ev.endAt) < new Date() : false;
  const capStr = ev.capacity ? `${ev.counts?.confirmed || 0}/${ev.capacity}` : 'unbegrenzt';
  const regMode = ev.registrationMode || 'open';
  const safeDescriptionHtml = useMemo(() => {
    try {
      const raw = marked.parse(ev.description || '');
      return { __html: DOMPurify.sanitize(raw) };
    } catch { return { __html: '' }; }
  }, [ev?.description]);
  const [bcOpen, setBcOpen] = useState(false);
  const [bcTitle, setBcTitle] = useState('');
  const [bcMessage, setBcMessage] = useState('');
  const [bcTargets, setBcTargets] = useState({ confirmed:true, interested:true, waitlisted:false });
  const [bcSending, setBcSending] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newPollOpen, setNewPollOpen] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['','']);
  const [pollMulti, setPollMulti] = useState(false);
  const [pollSaving, setPollSaving] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  async function addComment() {
    const text = newComment.trim();
    if (!text) return;
    try {
      const c = await EventsAPI.addComment(ev.id, text);
      setComments(list => [...list, c]);
      setNewComment('');
    } catch { alert('Kommentar konnte nicht gespeichert werden'); }
  }

  async function deleteComment(id) {
    try {
      await EventsAPI.deleteComment(ev.id, id);
      setComments(list => list.filter(x => x.id !== id));
    } catch { alert('Löschen fehlgeschlagen'); }
  }

  function updatePollOption(idx, val) {
    setPollOptions(arr => arr.map((t,i)=> i===idx ? val : t));
  }
  function addPollOption() {
    setPollOptions(arr => [...arr, '']);
  }
  async function createPoll() {
    const opts = pollOptions.map(s=>s.trim()).filter(Boolean);
    if (!pollQuestion.trim() || opts.length < 2) return alert('Frage und mindestens zwei Optionen angeben');
    try {
      setPollSaving(true);
      await EventPollsAPI.create(ev.id, { question: pollQuestion.trim(), options: opts, multi: pollMulti });
      setPollQuestion(''); setPollOptions(['','']); setPollMulti(false); setNewPollOpen(false);
      if (typeof reload === 'function') reload();
    } catch { alert('Umfrage konnte nicht erstellt werden'); }
    finally { setPollSaving(false); }
  }
  async function vote(pollId, optionId) {
    try { await EventPollsAPI.vote(ev.id, pollId, optionId); if (typeof reload === 'function') reload(); }
    catch { alert('Abstimmung fehlgeschlagen'); }
  }
  async function closePoll(pollId) {
    try { await EventPollsAPI.close(ev.id, pollId); if (typeof reload === 'function') reload(); }
    catch { alert('Schließen fehlgeschlagen'); }
  }
  async function deletePoll(pollId) {
    try { await EventPollsAPI.remove(ev.id, pollId); setPolls(list => list.filter(p => p.id !== pollId)); }
    catch { alert('Löschen fehlgeschlagen'); }
  }

  async function sendBroadcast() {
    const targets = Object.entries(bcTargets).filter(([,v])=>v).map(([k])=>k);
    if (!bcTitle.trim()) return alert('Titel angeben');
    try {
      setBcSending(true);
      await EventsAPI.broadcast(ev.id, { title: bcTitle.trim(), message: bcMessage, targets });
      setBcOpen(false); setBcTitle(''); setBcMessage('');
      if (typeof reload === 'function') reload();
      alert('Nachricht versendet');
    } catch {
      alert('Senden fehlgeschlagen');
    } finally { setBcSending(false); }
  }

  return (
    <div>
      <div style={{ color:'#9ca3af', fontSize:13 }}>{formatDateRange(ev.startAt, ev.endAt)} · {ev.visibility}</div>
      {ev.posterUrl && (
        <Link to={`/events/${ev.id}`}>
          <div style={{ width:'100%', background:'#111', borderRadius:8, overflow:'hidden', marginTop:8, display:'flex', alignItems:'center', justifyContent:'center', maxHeight:'40vh' }}>
            <img src={resolveMediaUrl(ev.posterUrl)} alt={ev.title} style={{ maxWidth:'100%', maxHeight:'40vh', width:'auto', height:'auto' }} />
          </div>
        </Link>
      )}
      {/* Comments */}
      <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:10, marginTop:12 }}>
        <div style={{ fontWeight:700, marginBottom:6 }}>Kommentare</div>
        <div style={{ color:'#9ca3af', fontSize:13, maxHeight:220, overflow:'auto' }}>
          {(!comments || comments.length === 0) ? 'Keine Kommentare.' : comments.map(c => {
            const author = c.displayName || c.username || (c.userId ? `#${c.userId}` : 'Gast');
            const when = c.createdAt ? formatDateTime(c.createdAt) : '';
            return (
              <div key={c.id} style={{ padding:'6px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'center' }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {c.avatarUrl && (
                      <img src={resolveMediaUrl(c.avatarUrl)} alt="avatar" style={{ width:22, height:22, borderRadius:'50%' }} />
                    )}
                    <div style={{ display:'flex', gap:8, alignItems:'baseline' }}>
                      <div style={{ fontWeight:600 }}>{author}</div>
                      {when && <span style={{ fontSize:12, color:'#9ca3af' }}>{when}</span>}
                    </div>
                  </div>
                  {(isAdmin || isOwner || c.userId === currentUser?.id) && (
                    <button onClick={()=>deleteComment(c.id)} style={{ fontSize:12 }}>Löschen</button>
                  )}
                </div>
                <div style={{ color:'#cbd5e1', whiteSpace:'pre-wrap' }}>{c.body}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8, marginTop:8 }}>
          <input placeholder="Kommentar schreiben…" value={newComment} onChange={e=>setNewComment(e.target.value)} />
          <button onClick={addComment}>Senden</button>
        </div>
      </div>
      <div style={{ marginTop:10 }}>
        <div style={{ fontWeight:700 }}>Beschreibung</div>
        {ev.description ? (
          <div style={{ color:'#cbd5e1' }} dangerouslySetInnerHTML={safeDescriptionHtml} />
        ) : (
          <div style={{ color:'#9ca3af' }}>—</div>
        )}
      </div>
      {(ev.locationType === 'venue' && ev.venueAddress) && (
        <div style={{ marginTop:10 }}>
          <div style={{ fontWeight:700 }}>Ort</div>
          <div style={{ color:'#cbd5e1' }}>{ev.venueAddress}</div>
        </div>
      )}
      {(ev.locationType === 'online' && ev.onlineUrl) && (
        <div style={{ marginTop:10 }}>
          <div style={{ fontWeight:700 }}>Online</div>
          <a href={ev.onlineUrl} target="_blank" rel="noreferrer">Zum Event-Link</a>
        </div>
      )}
      <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:10, marginTop:12 }}>
        <div style={{ fontWeight:700 }}>Teilnahme</div>
        <div style={{ color:'#9ca3af', fontSize:13, margin:'6px 0' }}>Kapazität: {capStr}</div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button onClick={()=>onAction('going')} disabled={ended || regMode==='ticket'}>Ich komme</button>
          <button onClick={()=>onAction('interested')} disabled={ended || regMode==='ticket'}>Interessiert</button>
          <button onClick={()=>onAction('decline')} disabled={ended}>Absagen</button>
        </div>
        {regMode==='ticket' && (
          <div style={{ color:'#f59e0b', fontSize:12, marginTop:6 }}>Tickets erforderlich – Anmeldung derzeit deaktiviert</div>
        )}
        {regMode==='invite' && (
          <div style={{ color:'#9ca3af', fontSize:12, marginTop:6 }}>Anmeldung nur auf Einladung – du kannst dein Interesse markieren</div>
        )}
        {(isAdmin || isOwner) && (
          <div style={{ marginTop:8 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <Link to={`/events/${ev.id}`}><button>Event öffnen</button></Link>
              <Link to={`/events/${ev.id}/edit`}><button>Event bearbeiten</button></Link>
            </div>
          </div>
        )}
      </div>
      {(ev.participantListPublic || isAdmin || isOwner) && (
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:10, marginTop:12 }}>
          <div style={{ fontWeight:700 }}>Teilnehmer</div>
          <div style={{ color:'#9ca3af', fontSize:12, marginTop:6, maxHeight:200, overflow:'auto' }}>
            {attendees.length === 0 ? 'Keine Anmeldungen.' : attendees.map(a => {
              const label = a.displayName || a.username || a.externalEmail || (a.userId ? `#${a.userId}` : 'Gast');
              const right = a.isExternalGroup ? (`x ${a.externalCount}`) : a.status;
              return (
                <div key={a.id} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                  <div>{label}</div>
                  <div style={{ color:'#cbd5e1' }}>{right}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {(isAdmin || isOwner) && (
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:10, marginTop:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight:700 }}>Aktivitäten</div>
            <button onClick={()=>setLogsOpen(v=>!v)} style={{ fontSize:12 }}>{logsOpen ? 'Schließen' : 'Öffnen'}</button>
          </div>
          {logsOpen && (
            <div style={{ marginTop:8, maxHeight:220, overflow:'auto', color:'#9ca3af', fontSize:12 }}>
              {(logs && logs.length > 0) ? logs.map(l => (
                <div key={l.id} style={{ padding:'6px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:8, alignItems:'start' }}>
                    <span style={{ color:'#cbd5e1' }}>{formatDateTime(l.createdAt)}</span>
                    <span>{renderLogLine(l)}</span>
                  </div>
                </div>
              )) : 'Keine Aktivitäten.'}
            </div>
          )}
        </div>
      )}
      {(isAdmin || isOwner) && (
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:10, marginTop:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight:700 }}>Nachricht an Teilnehmer</div>
            <button onClick={()=>setBcOpen(v=>!v)}>{bcOpen ? 'Schließen' : 'Öffnen'}</button>
          </div>
          {bcOpen && (
            <div style={{ marginTop:8, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              <input placeholder="Titel" value={bcTitle} onChange={e=>setBcTitle(e.target.value)} style={{ gridColumn:'1/-1' }} />
              <textarea placeholder="Nachricht" value={bcMessage} onChange={e=>setBcMessage(e.target.value)} style={{ gridColumn:'1/-1', minHeight:100 }} />
              <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={bcTargets.confirmed} onChange={e=>setBcTargets(s=>({...s,confirmed:e.target.checked}))} /> Zugesagt</label>
              <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={bcTargets.interested} onChange={e=>setBcTargets(s=>({...s,interested:e.target.checked}))} /> Interessiert</label>
              <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={bcTargets.waitlisted} onChange={e=>setBcTargets(s=>({...s,waitlisted:e.target.checked}))} /> Warteliste</label>
              <div style={{ gridColumn:'1/-1' }}>
                <button onClick={sendBroadcast} disabled={bcSending}>{bcSending ? 'Senden…' : 'Senden'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Polls */}
      <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:10, marginTop:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700 }}>Umfragen</div>
          {(isAdmin || isOwner) && <button onClick={()=>setNewPollOpen(v=>!v)}>{newPollOpen ? 'Neue Umfrage schließen' : 'Neue Umfrage'}</button>}
        </div>
        {newPollOpen && (isAdmin || isOwner) && (
          <div style={{ marginTop:8 }}>
            <input placeholder="Frage" value={pollQuestion} onChange={e=>setPollQuestion(e.target.value)} style={{ width:'100%', marginBottom:6 }} />
            {pollOptions.map((opt, idx) => (
              <input key={idx} placeholder={`Option ${idx+1}`} value={opt} onChange={e=>updatePollOption(idx, e.target.value)} style={{ width:'100%', marginBottom:6 }} />
            ))}
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <button onClick={addPollOption}>Option hinzufügen</button>
              <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={pollMulti} onChange={e=>setPollMulti(e.target.checked)} /> Mehrfachauswahl</label>
              <button onClick={createPoll} disabled={pollSaving}>{pollSaving ? 'Erstellen…' : 'Erstellen'}</button>
            </div>
          </div>
        )}
        <div style={{ marginTop:8 }}>
          {(!polls || polls.length === 0) ? (
            <div style={{ color:'#9ca3af', fontSize:13 }}>Keine Umfragen.</div>
          ) : polls.map(p => {
            const closed = p.closesAt && new Date(p.closesAt) < new Date();
            const total = p.options.reduce((acc,o)=>acc+o.votes,0);
            return (
              <div key={p.id} style={{ padding:'8px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                  <div style={{ fontWeight:600 }}>{p.question}</div>
                  {(isAdmin || isOwner) && (
                    <div style={{ display:'flex', gap:8 }}>
                      {!closed && <button onClick={()=>closePoll(p.id)} style={{ fontSize:12 }}>Beenden</button>}
                      <button onClick={()=>deletePoll(p.id)} style={{ fontSize:12 }}>Löschen</button>
                    </div>
                  )}
                </div>
                <div style={{ color:'#9ca3af', fontSize:12, marginTop:2 }}>{p.multi ? 'Mehrfachauswahl' : 'Einzelauswahl'}{closed ? ' · beendet' : ''}</div>
                <div style={{ marginTop:6, display:'grid', gap:6 }}>
                  {p.options.map(o => {
                    const pct = total ? Math.round((o.votes/total)*100) : 0;
                    const chosen = !!o.chosen;
                    return (
                      <div key={o.id} style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8, alignItems:'center' }}>
                        <div>
                          <div style={{ fontSize:14, color:'#cbd5e1' }}>{o.text}</div>
                          <div style={{ height:6, background:'rgba(255,255,255,0.06)', borderRadius:4, overflow:'hidden', marginTop:4 }}>
                            <div style={{ width:`${pct}%`, height:6, background: chosen ? '#32CD89' : '#6b7280' }} />
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ color:'#9ca3af', fontSize:12 }}>{o.votes} ({pct}%)</div>
                          {!closed && <button onClick={()=>vote(p.id, o.id)} style={{ fontSize:12 }}>{chosen ? 'Gewählt' : 'Wählen'}</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function renderLogLine(l) {
  const action = String(l.action || '');
  const map = {
    'create': 'Event erstellt',
    'update': 'Event bearbeitet',
    'cancel': 'Event abgesagt',
    'poster': 'Poster aktualisiert',
    'invite': 'Einladung gesendet',
  };
  if (action.startsWith('register:')) {
    const a = action.split(':')[1];
    return `Teilnahmestatus: ${a}`;
  }
  if (action.startsWith('poll:')) {
    const a = action.split(':')[1];
    if (a === 'create') return 'Umfrage erstellt';
    if (a === 'vote') return 'Abstimmung abgegeben';
    if (a === 'close') return 'Umfrage beendet';
    if (a === 'delete') return 'Umfrage gelöscht';
  }
  if (action === 'comment') return 'Neuer Kommentar';
  if (action === 'comment:delete') return 'Kommentar gelöscht';
  if (action === 'broadcast') return 'Nachricht an Teilnehmer gesendet';
  return map[action] || action;
}

function formatDateTime(ts) {
  try {
    const d = new Date(ts);
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(d);
  } catch { return String(ts); }
}
